import { v4 as uuidv4 } from "uuid";
import {
  type CsvQuestionInputItem,
  DIFY_RESULT_CSV_HEADERS,
  type DifyAPITestJob,
  type DifyAPITestRun,
  type DifyQuestionInputItem,
  type DifyResultCsvRow,
  type DifySubmitPayload,
} from "~/components/difyAPITest/difyAPITest.types";
import { buildCsvText } from "~/utils/csvUtils";
import { formatDateTimeForFileName } from "~/utils/difyAPITest/difyAPITestDateTimeUtils";

export const DEFAULT_DIFY_APP_NAME = "dify-api-test";

/**
 * Difyアプリ名をCSVファイル名向けに正規化する
 * @param appName Difyアプリ名
 * @return 使用可能なアプリ名
 */
export const normalizeDifyAppName = (appName?: string) => {
  const normalizedAppName = appName?.trim() ?? "";

  return normalizedAppName.length > 0 ? normalizedAppName : DEFAULT_DIFY_APP_NAME;
};

/**
 * CSV入力項目かどうかを判定する
 * @param item 質問入力項目
 * @return CSV入力項目かどうか
 */
export const isCsvQuestionInputItem = (
  item: DifyQuestionInputItem
): item is CsvQuestionInputItem => {
  return "expectedAnswer" in item;
};

/**
 * 実行タイトルを生成する
 * @param questions 質問入力一覧
 * @return 実行タイトル
 */
export const createRunTitle = (questions: DifyQuestionInputItem[]) => {
  if (questions.length === 0) {
    return "質問未設定";
  }

  const firstQuestion = questions[0]?.question ?? "質問未設定";

  if (questions.length === 1) {
    return firstQuestion;
  }

  return `${firstQuestion} ほか ${questions.length - 1} 件`;
};

/**
 * 送信ペイロードから実行情報を生成する
 * @param payload 送信ペイロード
 * @param appName Difyアプリ名
 * @return 実行情報
 */
export const createRunFromPayload = (
  payload: DifySubmitPayload,
  appName = DEFAULT_DIFY_APP_NAME
): DifyAPITestRun => {
  const createdAt = new Date();

  return {
    runId: uuidv4(),
    appName: normalizeDifyAppName(appName),
    title: createRunTitle(payload.questions),
    inputMethod: payload.inputMethod,
    status: "running",
    createdAt,
    finishedAt: null,
    jobs: payload.questions.map((item) => createJobFromQuestion(item, createdAt)),
  };
};

/**
 * 質問入力からジョブ情報を生成する
 * @param item 質問入力項目
 * @param startedAt 開始日時
 * @return ジョブ情報
 */
export const createJobFromQuestion = (
  item: DifyQuestionInputItem,
  startedAt: Date | null = null
): DifyAPITestJob => {
  return {
    jobId: uuidv4(),
    sessionId: uuidv4(),
    question: item.question,
    expectedAnswer: isCsvQuestionInputItem(item) ? item.expectedAnswer : "",
    status: "queued",
    chunks: [],
    aiOutput: "",
    messageId: "",
    errorMessage: "",
    startedAt,
    completedAt: null,
  };
};

/**
 * ジョブ情報をCSV行へ変換する
 * @param job ジョブ情報
 * @return CSV行データ
 */
export const toCsvRow = (job: DifyAPITestJob): DifyResultCsvRow => {
  return {
    question: job.question,
    expectedAnswer: job.expectedAnswer,
    aiOutput: job.aiOutput,
    judgment: "",
    messageId: job.messageId,
    note: job.errorMessage,
  };
};

/**
 * 実行情報からCSV行一覧を生成する
 * @param run 実行情報
 * @return CSV行一覧
 */
export const buildCsvRowsFromRun = (run: DifyAPITestRun): DifyResultCsvRow[] => {
  return run.jobs.map((job) => toCsvRow(job));
};

/**
 * 実行情報からCSVファイル名を生成する
 * @param run 実行情報
 * @return CSVファイル名
 */
export const buildRunCsvFileName = (run: Pick<DifyAPITestRun, "appName" | "createdAt">) => {
  return `${normalizeDifyAppName(run.appName)}-${formatDateTimeForFileName(run.createdAt)}.csv`;
};

/**
 * CSV行一覧からCSV文字列を生成する
 * @param rows CSV行一覧
 * @return CSV文字列
 */
export const buildDifyResultCsvText = (rows: DifyResultCsvRow[]) => {
  return buildCsvText(
    Object.values(DIFY_RESULT_CSV_HEADERS),
    rows.map((row) => [
      row.question,
      row.expectedAnswer,
      row.aiOutput,
      row.judgment,
      row.messageId,
      row.note,
    ])
  );
};
